import React, { useState } from 'react';
import classnames from 'classnames';
import styles from '../ItemsList/ItemsList.module.scss';
import { OptionsButton } from '../OptionsButton/OptionsButton';
import { InlineEditor } from '../InlineEditor/InlineEditor';
import { IDocumentItem } from './DocumentItem.interface';
import {
  useAppSelector,
  useAppDispatch,
  getSelectedDocuments,
  getCanSelectDocuments,
  setSelectedDocuments,
  setCurrentFile,
  getBusinessArea,
  getService
} from 'app/redux';
import { toggleDocumentInArray } from 'app/utils';
import { downloadDocument } from 'integration';
import { TranslateDocumentModal } from 'app/components/Modals';
import { Language } from 'app/components/LanguageSelector';
import { Tooltip } from 'react-tooltip';
import { SERVICE_NAMES } from 'app/utils/consts';

export const DocumentItem: React.FC<IDocumentItem> = ({
  documentName,
  documentPath,
  isEditing,
  documentActions,
  maxDisplayLength = 25
}) => {
  const dispatch = useAppDispatch();
  const selectedDocuments = useAppSelector(getSelectedDocuments);
  const canSelectDocuments = useAppSelector(getCanSelectDocuments);
  const isSelected = selectedDocuments.includes(documentPath);
  const [isTranslateModalOpen, setIsTranslateModalOpen] = useState(false);
  const shouldShowTooltip = documentName.length > maxDisplayLength;
  const currentService =
    useAppSelector(getService) ?? SERVICE_NAMES.ASK_YOUR_DOCS;
  const currentBusinessArea =
    useAppSelector(getBusinessArea) ?? 'cross-functional';

  const handleCheckboxChange = (): void => {
    if (!canSelectDocuments) return;
    const newSelection = toggleDocumentInArray(selectedDocuments, documentPath);
    dispatch(setSelectedDocuments(newSelection));
  };

  const handlePreview = (): void => {
    downloadDocument(currentBusinessArea, currentService, documentPath, true)
      .then((response) => {
        const blob = new Blob([Uint8Array.from(response.data)], {
          type: 'application/pdf'
        });
        const file = new File([blob], `${documentName}.pdf`, {
          type: 'application/pdf'
        });
        dispatch(setCurrentFile(file));
      })
      .catch((error) => {
        console.error('Error loading document preview:', error);
      });
  };

  const handleTranslate = (language: Language): void => {
    documentActions.onTranslate?.(documentPath, language);
  };

  return (
    <div className={styles['document-item']}>
      <label className={styles['document-checkbox']}>
        <input
          type="checkbox"
          value={documentName}
          checked={isSelected}
          disabled={!canSelectDocuments}
          onChange={handleCheckboxChange}
        />
        <span className={styles.checkmark}></span>
        {isEditing ? (
          <InlineEditor
            value={documentName}
            onSave={(newName) =>
              documentActions.onRename?.(documentPath, newName)
            }
            onCancel={documentActions.onCancelEditing || (() => {})}
            placeholder={documentName}
          />
        ) : (
          <span
            className={classnames({
              [styles['shortened-title']]: shouldShowTooltip
            })}
            {...(shouldShowTooltip && {
              'data-tooltip-id': 'document-name-tooltip',
              'data-tooltip-content': documentName
            })}
          >
            {shouldShowTooltip
              ? `${documentName.slice(0, maxDisplayLength)}...`
              : documentName}
          </span>
        )}
      </label>
      <OptionsButton
        variant="document"
        onEdit={() => documentActions.onEdit?.(documentPath)}
        onDelete={() => documentActions.onDelete?.(documentPath)}
        onDownload={handlePreview}
        onTranslate={() => setIsTranslateModalOpen(true)}
      />

      <TranslateDocumentModal
        isOpen={isTranslateModalOpen}
        onClose={() => setIsTranslateModalOpen(false)}
        onTranslate={handleTranslate}
        documentName={documentName}
      />

      <Tooltip
        id="document-name-tooltip"
        className={styles['shortened-title-tooltip']}
        noArrow
      />
    </div>
  );
};
